import { useQuery } from '@tanstack/react-query';
import { directoryApi, type RoadLeg, type RoadLegQuery, type TruckProfile } from '../client';

export const roadLegKeys = {
  all: ['directory', 'roadLegs'] as const,
  /**
   * Keyed the way Directory's route cache keys a leg: a Google place by its
   * place id, anything else by coordinates rounded to about 100 m (two
   * collections at one industrial estate are the same leg), plus the vehicle.
   */
  batch: (organisationId: string | null, legs: RoadLegQuery[], vehicle?: TruckProfile | null) =>
    [
      'directory',
      'roadLegs',
      organisationId,
      legs.map(
        (l) =>
          `${l.fromPlaceId ?? `${l.fromLatitude.toFixed(3)},${l.fromLongitude.toFixed(3)}`}>` +
          `${l.toPlaceId ?? `${l.toLatitude.toFixed(3)},${l.toLongitude.toFixed(3)}`}`,
      ),
      vehicle ?? null,
    ] as const,
};

/**
 * Truck driving times for a batch of road legs, from Directory's route cache
 * (Azure Maps in truck mode, ferries avoided). A leg Azure could not route
 * comes back with null times — keep showing the estimate, marked as one.
 *
 * <p>
 * Long-lived here too: roads do not move, and the server keeps the answer for
 * up to six months anyway. Disabled when there are no legs, so a journey with
 * ungeocoded stops never calls out.
 * </p>
 */
export function useRoadLegs(
  organisationId: string | null,
  legs: RoadLegQuery[],
  vehicle?: TruckProfile | null,
) {
  return useQuery<RoadLeg[], Error>({
    queryKey: roadLegKeys.batch(organisationId, legs, vehicle),
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getRoadLegs({ organisationId, legs, vehicle });
    },
    enabled: !!organisationId && legs.length > 0,
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    // An outage leaves the panel on its estimates rather than hammering a
    // provider that is already unhappy.
    retry: 1,
  });
}
